import { useState } from "react";
import { Crown, TrendingUp, Calendar, Trophy, Activity, Lock, ChevronRight, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import type { FavoritePlayer } from "@/hooks/useUserFavorites";
import type { LivePlayerStats } from "@/hooks/useFavoritePlayerStats";
import { usePlayerCareerStats } from "@/hooks/usePlayerCareerStats";

// Flag mapping for nationalities
const NATIONALITY_FLAGS: Record<string, string> = {
  USA: "🇺🇸", CAN: "🇨🇦", SWE: "🇸🇪", FIN: "🇫🇮", RUS: "🇷🇺",
  CZE: "🇨🇿", GER: "🇩🇪", SUI: "🇨🇭", SVK: "🇸🇰", LAT: "🇱🇻",
  AUT: "🇦🇹", DEN: "🇩🇰", NOR: "🇳🇴", FRA: "🇫🇷", GBR: "🇬🇧",
};

// Game log interface (sample data - API doesn't provide game-by-game stats)
interface GameLog {
  date: string;
  opponent: string;
  result: string;
  goals: number;
  assists: number;
  points: number;
  plusMinus: number;
  toi: string;
  shots: number;
}

// Generate sample game logs (API-Sports doesn't provide game-by-game player stats)
const generateGameLogs = (player: FavoritePlayer): GameLog[] => {
  const logs: GameLog[] = [];
  const opponents = ["TOR", "MTL", "NYR", "BOS", "DET", "CHI", "PIT", "WSH", "TBL", "FLA"];
  const now = new Date();
  
  for (let i = 0; i < 10; i++) {
    const date = new Date(now);
    date.setDate(date.getDate() - (i * 2 + 1));
    const goals = Math.random() < 0.3 ? Math.floor(Math.random() * 2) + 1 : 0;
    const assists = Math.random() < 0.4 ? Math.floor(Math.random() * 2) + 1 : 0;
    const homeGoals = Math.floor(Math.random() * 5) + 1;
    const awayGoals = Math.floor(Math.random() * 5) + 1;
    
    logs.push({
      date: date.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      opponent: opponents[Math.floor(Math.random() * opponents.length)],
      result: homeGoals > awayGoals ? `W ${homeGoals}-${awayGoals}` : `L ${awayGoals}-${homeGoals}`,
      goals,
      assists,
      points: goals + assists,
      plusMinus: Math.floor(Math.random() * 5) - 2,
      toi: `${Math.floor(Math.random() * 8) + 14}:${String(Math.floor(Math.random() * 60)).padStart(2, "0")}`,
      shots: Math.floor(Math.random() * 6) + 1,
    });
  }
  
  return logs;
};

interface PremiumPlayerStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  player: FavoritePlayer | null;
  currentStats?: LivePlayerStats | null;
}

// Loading skeleton for career stats
function CareerStatsSkeleton() {
  return (
    <div className="space-y-4">
      <div className="bg-secondary/30 rounded-xl p-4 border border-border">
        <Skeleton className="h-4 w-32 mb-3" />
        <div className="grid grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="text-center">
              <Skeleton className="h-8 w-12 mx-auto mb-1" />
              <Skeleton className="h-3 w-8 mx-auto" />
            </div>
          ))}
        </div>
      </div>
      <div className="bg-secondary/30 rounded-xl border border-border overflow-hidden">
        <div className="p-3 bg-secondary/50">
          <Skeleton className="h-4 w-full" />
        </div>
        {[...Array(4)].map((_, i) => (
          <div key={i} className="p-3 border-t border-border/50">
            <Skeleton className="h-4 w-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function PremiumPlayerStatsModal({ isOpen, onClose, player, currentStats }: PremiumPlayerStatsModalProps) {
  const { subscription, user } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");

  if (!player) return null;

  const flag = player.nationality ? NATIONALITY_FLAGS[player.nationality] || "" : "";
  const isGoalie = player.position === "G" || currentStats?.isGoalie;
  
  // Premium tiers that get access
  const hasPremiumAccess = subscription.subscribed && (subscription.tier === "pro" || subscription.tier === "elite");
  
  // Fetch real career stats from API
  const { seasons: careerStats, totals: careerTotals, isLoading, error } = usePlayerCareerStats(
    hasPremiumAccess ? player : null
  );
  
  // Generate sample game logs (API doesn't support game-by-game)
  const gameLogs = generateGameLogs(player);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col bg-background border-border">
        <DialogHeader className="pb-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {player.photo ? (
                <img
                  src={player.photo}
                  alt={player.name}
                  className="w-16 h-16 rounded-full object-cover bg-secondary ring-2 ring-primary/30"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center text-lg font-bold text-muted-foreground ring-2 ring-primary/30">
                  {player.name.split(" ").map(n => n[0]).join("").slice(0, 2)}
                </div>
              )}
              <div>
                <div className="flex items-center gap-2">
                  {flag && <span className="text-lg">{flag}</span>}
                  <DialogTitle className="text-xl font-display">{player.name}</DialogTitle>
                  {hasPremiumAccess && (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 text-amber-400 rounded-full text-xs font-medium">
                      <Crown className="w-3 h-3" />
                      Premium
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  <span className="px-1.5 py-0.5 bg-primary/10 text-primary rounded font-medium text-xs mr-2">
                    {player.position}
                  </span>
                  {player.team} · {player.league}
                </p>
              </div>
            </div>
          </div>
        </DialogHeader>

        {!hasPremiumAccess ? (
          // Upgrade prompt for non-premium users
          <div className="flex-1 flex flex-col items-center justify-center py-12 px-6 text-center">
            <div className="p-4 bg-gradient-to-br from-amber-500/10 to-yellow-500/10 rounded-full mb-6">
              <Lock className="w-12 h-12 text-amber-400" />
            </div>
            <h3 className="font-display text-2xl font-bold text-foreground mb-2">
              Unlock Premium Player Stats
            </h3>
            <p className="text-muted-foreground mb-6 max-w-md">
              Get detailed career statistics, game-by-game logs, performance trends, and advanced analytics 
              with a Pro or Elite subscription.
            </p>
            
            <div className="grid grid-cols-3 gap-4 mb-8 text-sm">
              <div className="p-4 bg-secondary/50 rounded-lg border border-border">
                <Trophy className="w-6 h-6 text-primary mx-auto mb-2" />
                <p className="font-medium text-foreground">Career Stats</p>
                <p className="text-xs text-muted-foreground">Full history</p>
              </div>
              <div className="p-4 bg-secondary/50 rounded-lg border border-border">
                <Calendar className="w-6 h-6 text-primary mx-auto mb-2" />
                <p className="font-medium text-foreground">Game Logs</p>
                <p className="text-xs text-muted-foreground">Last 10 games</p>
              </div>
              <div className="p-4 bg-secondary/50 rounded-lg border border-border">
                <TrendingUp className="w-6 h-6 text-primary mx-auto mb-2" />
                <p className="font-medium text-foreground">Trends</p>
                <p className="text-xs text-muted-foreground">Performance data</p>
              </div>
            </div>
            
            <a
              href="/#pricing"
              onClick={onClose}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-semibold rounded-lg hover:from-amber-400 hover:to-yellow-400 transition-all"
            >
              <Crown className="w-4 h-4" />
              Upgrade to Pro
              <ChevronRight className="w-4 h-4" />
            </a>
            
            {!user && (
              <p className="text-xs text-muted-foreground mt-4">
                Already subscribed? <a href="/auth" className="text-primary hover:underline">Sign in</a>
              </p>
            )}
          </div>
        ) : (
          // Premium content
          <div className="flex-1 overflow-hidden flex flex-col">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
              <TabsList className="grid w-full grid-cols-3 mb-4">
                <TabsTrigger value="overview" className="flex items-center gap-2">
                  <Activity className="w-4 h-4" />
                  Overview
                </TabsTrigger>
                <TabsTrigger value="career" className="flex items-center gap-2">
                  <Trophy className="w-4 h-4" />
                  Career
                </TabsTrigger>
                <TabsTrigger value="games" className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Game Log
                </TabsTrigger>
              </TabsList>

              <div className="flex-1 overflow-y-auto">
                {/* Overview Tab */}
                <TabsContent value="overview" className="mt-0 space-y-6">
                  {/* Current Season Stats */}
                  <div className="bg-secondary/30 rounded-xl p-4 border border-border">
                    <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                      Current Season
                    </h4>
                    {currentStats && !isGoalie ? (
                      <div className="grid grid-cols-5 gap-4 text-center">
                        <div>
                          <p className="text-2xl font-display font-bold text-foreground">{currentStats.gp}</p>
                          <p className="text-xs text-muted-foreground">GP</p>
                        </div>
                        <div>
                          <p className="text-2xl font-display font-bold text-foreground">{currentStats.goals}</p>
                          <p className="text-xs text-muted-foreground">Goals</p>
                        </div>
                        <div>
                          <p className="text-2xl font-display font-bold text-foreground">{currentStats.assists}</p>
                          <p className="text-xs text-muted-foreground">Assists</p>
                        </div>
                        <div>
                          <p className="text-2xl font-display font-bold text-primary">{currentStats.points}</p>
                          <p className="text-xs text-muted-foreground">Points</p>
                        </div>
                        <div>
                          <p className={`text-2xl font-display font-bold ${currentStats.plusMinus >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {currentStats.plusMinus >= 0 ? '+' : ''}{currentStats.plusMinus}
                          </p>
                          <p className="text-xs text-muted-foreground">+/-</p>
                        </div>
                      </div>
                    ) : currentStats && isGoalie ? (
                      <div className="grid grid-cols-4 gap-4 text-center">
                        <div>
                          <p className="text-2xl font-display font-bold text-foreground">{currentStats.gp}</p>
                          <p className="text-xs text-muted-foreground">GP</p>
                        </div>
                        <div>
                          <p className="text-2xl font-display font-bold text-foreground">{currentStats.wins || 0}</p>
                          <p className="text-xs text-muted-foreground">Wins</p>
                        </div>
                        <div>
                          <p className="text-2xl font-display font-bold text-foreground">{currentStats.gaa?.toFixed(2) || "0.00"}</p>
                          <p className="text-xs text-muted-foreground">GAA</p>
                        </div>
                        <div>
                          <p className="text-2xl font-display font-bold text-primary">
                            {currentStats.svPct ? (currentStats.svPct * 100).toFixed(1) + "%" : "0%"}
                          </p>
                          <p className="text-xs text-muted-foreground">SV%</p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-center py-4">No current season stats available</p>
                    )}
                  </div>

                  {/* Career Totals */}
                  <div className="bg-secondary/30 rounded-xl p-4 border border-border">
                    <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                      Career Totals
                      {isLoading && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
                      {!isLoading && careerStats.length > 0 && (
                        <span className="text-xs font-normal text-muted-foreground">
                          ({careerStats.length} Seasons)
                        </span>
                      )}
                    </h4>
                    {isLoading ? (
                      <div className="grid grid-cols-5 gap-4">
                        {[...Array(5)].map((_, i) => (
                          <div key={i} className="text-center">
                            <Skeleton className="h-8 w-12 mx-auto mb-1" />
                            <Skeleton className="h-3 w-8 mx-auto" />
                          </div>
                        ))}
                      </div>
                    ) : careerStats.length > 0 ? (
                      <div className="grid grid-cols-5 gap-4 text-center">
                        <div>
                          <p className="text-2xl font-display font-bold text-foreground">{careerTotals.gp}</p>
                          <p className="text-xs text-muted-foreground">GP</p>
                        </div>
                        <div>
                          <p className="text-2xl font-display font-bold text-foreground">{careerTotals.goals}</p>
                          <p className="text-xs text-muted-foreground">Goals</p>
                        </div>
                        <div>
                          <p className="text-2xl font-display font-bold text-foreground">{careerTotals.assists}</p>
                          <p className="text-xs text-muted-foreground">Assists</p>
                        </div>
                        <div>
                          <p className="text-2xl font-display font-bold text-primary">{careerTotals.points}</p>
                          <p className="text-xs text-muted-foreground">Points</p>
                        </div>
                        <div>
                          <p className="text-2xl font-display font-bold text-foreground">{careerTotals.pim}</p>
                          <p className="text-xs text-muted-foreground">PIM</p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-center py-4">
                        {error ? "Failed to load career stats" : "No career data available"}
                      </p>
                    )}
                  </div>

                  {/* Points Per Game Trend */}
                  {careerStats.length > 0 && (
                    <div className="bg-secondary/30 rounded-xl p-4 border border-border">
                      <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                        Points Per Game Trend
                      </h4>
                      <div className="flex items-end justify-between h-24 gap-2">
                        {careerStats.slice(-6).map((season, idx) => {
                          const ppg = season.gp > 0 ? (season.points / season.gp) : 0;
                          const maxPpg = 1.5;
                          const height = Math.min((ppg / maxPpg) * 100, 100);
                          return (
                            <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                              <div 
                                className="w-full bg-primary/80 rounded-t transition-all hover:bg-primary"
                                style={{ height: `${height}%`, minHeight: "4px" }}
                                title={`${ppg.toFixed(2)} PPG`}
                              />
                              <span className="text-[10px] text-muted-foreground">{season.season.slice(-5)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* Career Tab */}
                <TabsContent value="career" className="mt-0">
                  {isLoading ? (
                    <CareerStatsSkeleton />
                  ) : careerStats.length > 0 ? (
                    <div className="bg-secondary/30 rounded-xl border border-border overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-secondary/50 border-b border-border">
                          <tr className="text-muted-foreground text-xs uppercase tracking-wider">
                            <th className="text-left p-3">Season</th>
                            <th className="text-left p-3">Team</th>
                            <th className="text-center p-3">GP</th>
                            <th className="text-center p-3">G</th>
                            <th className="text-center p-3">A</th>
                            <th className="text-center p-3">PTS</th>
                            <th className="text-center p-3">+/-</th>
                            <th className="text-center p-3">PIM</th>
                          </tr>
                        </thead>
                        <tbody>
                          {careerStats.map((season, idx) => (
                            <tr 
                              key={idx} 
                              className={`border-b border-border/50 hover:bg-secondary/30 ${idx === careerStats.length - 1 ? 'bg-primary/5' : ''}`}
                            >
                              <td className="p-3 font-medium">{season.season}</td>
                              <td className="p-3 flex items-center gap-2">
                                {season.teamLogo && (
                                  <img src={season.teamLogo} alt="" className="w-5 h-5 object-contain" />
                                )}
                                {season.team}
                              </td>
                              <td className="text-center p-3">{season.gp}</td>
                              <td className="text-center p-3">{season.goals}</td>
                              <td className="text-center p-3">{season.assists}</td>
                              <td className="text-center p-3 font-bold text-primary">{season.points}</td>
                              <td className={`text-center p-3 ${season.plusMinus >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                {season.plusMinus >= 0 ? '+' : ''}{season.plusMinus}
                              </td>
                              <td className="text-center p-3">{season.pim}</td>
                            </tr>
                          ))}
                          {/* Totals row */}
                          <tr className="bg-secondary/50 font-bold border-t-2 border-border">
                            <td className="p-3">Career</td>
                            <td className="p-3">-</td>
                            <td className="text-center p-3">{careerTotals.gp}</td>
                            <td className="text-center p-3">{careerTotals.goals}</td>
                            <td className="text-center p-3">{careerTotals.assists}</td>
                            <td className="text-center p-3 text-primary">{careerTotals.points}</td>
                            <td className="text-center p-3">-</td>
                            <td className="text-center p-3">{careerTotals.pim}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="bg-secondary/30 rounded-xl p-8 border border-border text-center">
                      <Trophy className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
                      <p className="text-muted-foreground">
                        {error ? "Failed to load career statistics" : "No career statistics available for this player"}
                      </p>
                    </div>
                  )}
                </TabsContent>

                {/* Game Log Tab */}
                <TabsContent value="games" className="mt-0">
                  <div className="bg-secondary/30 rounded-xl border border-border overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-secondary/50 border-b border-border">
                        <tr className="text-muted-foreground text-xs uppercase tracking-wider">
                          <th className="text-left p-3">Date</th>
                          <th className="text-left p-3">vs</th>
                          <th className="text-center p-3">Result</th>
                          <th className="text-center p-3">G</th>
                          <th className="text-center p-3">A</th>
                          <th className="text-center p-3">PTS</th>
                          <th className="text-center p-3">+/-</th>
                          <th className="text-center p-3">TOI</th>
                          <th className="text-center p-3">SOG</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gameLogs.map((game, idx) => (
                          <tr key={idx} className="border-b border-border/50 hover:bg-secondary/30">
                            <td className="p-3 text-muted-foreground">{game.date}</td>
                            <td className="p-3 font-medium">{game.opponent}</td>
                            <td className={`text-center p-3 font-medium ${game.result.startsWith('W') ? 'text-green-500' : 'text-red-500'}`}>
                              {game.result}
                            </td>
                            <td className="text-center p-3">{game.goals}</td>
                            <td className="text-center p-3">{game.assists}</td>
                            <td className={`text-center p-3 font-bold ${game.points > 0 ? 'text-primary' : ''}`}>
                              {game.points}
                            </td>
                            <td className={`text-center p-3 ${game.plusMinus >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                              {game.plusMinus >= 0 ? '+' : ''}{game.plusMinus}
                            </td>
                            <td className="text-center p-3 text-muted-foreground">{game.toi}</td>
                            <td className="text-center p-3">{game.shots}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-muted-foreground text-center mt-3">
                    Showing last 10 games · Sample data (API doesn't provide game-by-game stats)
                  </p>
                </TabsContent>
              </div>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default PremiumPlayerStatsModal;
